import React from "react";
import { FeatherCheck } from "@subframe/core";
import type { BooksMonthStatusItemEntity } from "../../../domain/entities/BooksHealthEntity";

export interface BooksMonthByMonthStatusSectionProps {
  months?: BooksMonthStatusItemEntity[];
  loading?: boolean;
}

/** Fixed width so tiles do not stretch when few months; up to 12 shown. */
const MONTH_TILE_CLASS =
  "h-[5.5rem] w-[4.5rem] shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5";

export function BooksMonthByMonthStatusSection({
  months = [],
  loading = false,
}: BooksMonthByMonthStatusSectionProps) {
  if (loading) {
    return (
      <div className="flex w-full min-w-0 flex-col items-stretch gap-4 self-stretch rounded-lg border border-solid border-neutral-200 bg-white px-6 py-5">
        <div className="h-4 w-48 animate-pulse rounded bg-neutral-200" />
        <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain sm:overflow-x-visible">
          <div className="flex min-w-full justify-start gap-2 sm:gap-3">
            {Array.from({ length: 12 }, (_, i) => (
              <div
                key={i}
                className={`flex animate-pulse rounded-lg bg-neutral-100 ${MONTH_TILE_CLASS}`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const tileBase = `flex ${MONTH_TILE_CLASS}`;
  const tileOpen = `${tileBase} border-2 border-solid border-neutral-900`;
  const tileClosed = `${tileBase} border border-solid border-neutral-200`;

  return (
    <div className="flex w-full min-w-0 flex-col items-stretch gap-4 self-stretch rounded-lg border border-solid border-neutral-200 bg-white px-6 py-5">
      <span className="text-caption-bold font-caption-bold text-neutral-500 uppercase tracking-wider">
        MONTH-BY-MONTH STATUS
      </span>
      <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain sm:overflow-x-visible">
        <div className="flex min-w-full justify-start gap-2 sm:gap-3">
          {months.slice(0, 12).map((m, i) => (
            <div
              key={`${m.monthLabel}-${m.yearLabel}-${i}`}
              aria-label={
                m.status === "closed"
                  ? `${m.monthLabel} ${m.yearLabel}, closed`
                  : `${m.monthLabel} ${m.yearLabel}, not closed`
              }
              className={m.status === "open" ? tileOpen : tileClosed}
            >
              <span className="text-caption font-caption leading-tight text-neutral-900">
                {m.monthLabel}
              </span>
              {m.yearLabel ? (
                <span className="text-caption font-caption leading-tight text-neutral-500">
                  {m.yearLabel}
                </span>
              ) : null}
              {m.status === "closed" ? (
                <FeatherCheck className="mt-0.5 text-body font-body text-success-600" />
              ) : (
                <span className="mt-0.5 text-body-bold font-body-bold text-neutral-400">
                  -
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
